import React, { useState, useEffect, useCallback, useContext } from "react";

import { Avatar, Chip, Popover, Typography, Tooltip, IconButton } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getContactDisplayName } from "../../helpers/contactDisplayName";
import ContactIdentificador from "../ContactIdentificador";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    padding: "6px 12px",
    minWidth: 0,
    flexWrap: "wrap",
  },
  avatar: {
    width: 36,
    height: 36,
    flexShrink: 0,
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    minWidth: 0,
    flexWrap: "wrap",
  },
  nameBlock: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    justifyContent: "center",
  },
  name: {
    fontWeight: 600,
    fontSize: 14,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    // feat_305: 160 -> 320 para que entre "Nombre Apellido — Empresa".
    // 280 se quedaba 5px corto justo en el caso real más común de co1
    // ("Marianela Mansilla — Expreso Biletta SRL" = 285px). Es max-width, no
    // width: el nameBlock tiene minWidth:0 dentro de un infoRow flexWrap, así
    // que en pantallas chicas el texto se achica solo y NO desborda (medido a
    // 390px de viewport). El string completo va igual en el title del Tooltip.
    maxWidth: 320,
  },
  presence: {
    fontSize: 12,
    fontStyle: "italic",
    color: "#00a884",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 200,
    lineHeight: 1.2,
  },
  // feat_295: subtexto "EN ATENCIÓN POR <asesor>" (naranja, estilo etiqueta).
  attendingBy: {
    fontSize: 11,
    fontWeight: 600,
    color: "#e65100",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 220,
    lineHeight: 1.2,
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  chip: {
    height: 18,
    fontSize: 11,
    cursor: "pointer",
  },
  separator: {
    color: "rgba(0,0,0,0.2)",
    fontSize: 16,
    lineHeight: 1,
    userSelect: "none",
    flexShrink: 0,
  },
  contactTagPill: {
    border: "1px dashed rgba(0,0,0,0.32)",
    background: "transparent",
    color: "rgba(0,0,0,0.55)",
    fontSize: 11,
    borderRadius: 20,
    padding: "2px 8px",
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    lineHeight: 1.6,
    position: "relative",
    "& $pillRemove": { display: "none" },
    "&:hover $pillRemove": { display: "inline-flex" },
  },
  pillRemove: {
    display: "none",
    cursor: "pointer",
    padding: 0,
    lineHeight: 1,
    color: "rgba(0,0,0,0.4)",
    "&:hover": { color: "rgba(0,0,0,0.7)" },
  },
  addTagBtn: {
    width: 20,
    height: 20,
    border: "1px dashed rgba(0,0,0,0.32)",
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "rgba(0,0,0,0.4)",
    flexShrink: 0,
    padding: 0,
    "&:hover": { borderColor: "rgba(0,0,0,0.6)", color: "rgba(0,0,0,0.7)", background: "rgba(0,0,0,0.04)" },
  },
  popoverPaper: {
    padding: theme.spacing(1),
    minWidth: 180,
    maxHeight: 240,
    overflowY: "auto",
  },
  tagPickerPaper: {
    padding: theme.spacing(1),
    minWidth: 200,
    maxHeight: 260,
    overflowY: "auto",
  },
  tagOption: {
    padding: "4px 8px",
    cursor: "pointer",
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    gap: 6,
    "&:hover": { backgroundColor: theme.palette.action.hover },
  },
  tagDot: {
    width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
  },
}));

const presenceText = (presence) => {
  if (!presence) return null;
  const { state, participantName, isGroup } = presence;
  const isComposing = state === "composing";
  if (isGroup) {
    if (participantName) {
      return i18n.t(
        isComposing
          ? "ticketPresence.composingNamed"
          : "ticketPresence.recordingNamed",
        { name: participantName }
      );
    }
    return i18n.t(
      isComposing
        ? "ticketPresence.composingSomeone"
        : "ticketPresence.recordingSomeone"
    );
  }
  return i18n.t(
    isComposing ? "ticketPresence.composing" : "ticketPresence.recording"
  );
};

const TicketInfo = ({ contact, ticket, presence, attendingMode = "off", onClick }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  // feat_295: mostrar quién atiende cuando el ticket open pertenece a OTRO asesor.
  const attendingByOther =
    attendingMode !== "off" &&
    ticket?.status === "open" &&
    ticket?.userId &&
    ticket.userId !== user?.id;
  const [ticketTags, setTicketTags] = useState(ticket?.tags || []);
  const [contactTags, setContactTags] = useState(contact?.tags || []);
  const [contactTypes, setContactTypes] = useState([]);
  const [typeAnchorEl, setTypeAnchorEl] = useState(null);
  const [localContactType, setLocalContactType] = useState(contact?.contactType || null);
  const [allTags, setAllTags] = useState([]);
  const [tagPickerAnchorEl, setTagPickerAnchorEl] = useState(null);

  useEffect(() => {
    setTicketTags(ticket?.tags || []);
  }, [ticket?.tags]);

  useEffect(() => {
    setLocalContactType(contact?.contactType || null);
  }, [contact?.contactType]);

  // Sync contact tags when contact changes (contact.tags viene del ticket response)
  useEffect(() => {
    setContactTags(contact?.tags || []);
  }, [contact?.id, contact?.tags]);

  const handleRemoveTicketTag = async (tagId) => {
    try {
      const { data } = await api.delete(`/tickets/${ticket.id}/tags/${tagId}`);
      setTicketTags(data);
    } catch (err) {
      toastError(err);
    }
  };

  const openTypePicker = async (e) => {
    e.stopPropagation();
    const anchor = e.currentTarget;
    setTypeAnchorEl(anchor);
    try {
      const { data } = await api.get("/contact-types");
      setContactTypes(data);
    } catch (err) {
      toastError(err);
    }
  };

  const handleSelectType = async (typeId) => {
    setTypeAnchorEl(null);
    const selectedType = typeId ? contactTypes.find(t => t.id === typeId) || null : null;
    try {
      await api.put(`/contacts/phone/${contact.number}/type`, { contactTypeId: typeId || null });
      setLocalContactType(selectedType);
    } catch (err) {
      toastError(err);
    }
  };

  const openTagPicker = useCallback(async (e) => {
    e.stopPropagation();
    setTagPickerAnchorEl(e.currentTarget);
    if (allTags.length === 0) {
      try {
        const { data } = await api.get("/tags");
        setAllTags(data);
      } catch (err) {
        toastError(err);
      }
    }
  }, [allTags.length]);

  const handleAddContactTag = useCallback(async (tagId) => {
    // optimistic
    const tag = allTags.find(t => t.id === tagId);
    if (!tag) return;
    setContactTags(prev => prev.some(t => t.id === tagId) ? prev : [...prev, tag]);
    setTagPickerAnchorEl(null);
    try {
      const { data } = await api.post(`/contacts/${contact.id}/tags/${tagId}`);
      setContactTags(data);
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.warning("Tag ya asignado");
      } else {
        // revert
        setContactTags(prev => prev.filter(t => t.id !== tagId));
        toastError(err);
      }
    }
  }, [allTags, contact?.id]);

  const handleRemoveContactTag = useCallback(async (e, tagId) => {
    e.stopPropagation();
    // optimistic
    setContactTags(prev => prev.filter(t => t.id !== tagId));
    try {
      const { data } = await api.delete(`/contacts/${contact.id}/tags/${tagId}`);
      setContactTags(data);
    } catch (err) {
      // revert
      const tag = allTags.find(t => t.id === tagId);
      if (tag) setContactTags(prev => [...prev, tag]);
      toastError(err);
    }
  }, [allTags, contact?.id]);

  // feat_305 — "Nombre Apellido — Empresa" (apellido/empresa opcionales).
  // isGroup sale del ticket: el include de contact no trae esa columna.
  const contactDisplayName = getContactDisplayName(contact, {
    isGroup: ticket?.isGroup,
  });

  const visibleContactTags = contactTags.slice(0, 3);
  const extraContactTags = contactTags.length - 3;

  return (
    <div className={classes.root} onClick={onClick}>
      {/* Avatar con tooltip */}
      <Tooltip title={`${contactDisplayName} · ver datos del contacto`} arrow>
        <Avatar
          src={contact.profilePicUrl}
          alt="contact_image"
          className={classes.avatar}
        />
      </Tooltip>

      {/* Fila de info */}
      <div className={classes.infoRow}>
        {/* Nombre + subtexto de presencia (Escribiendo/Grabando) estilo WA Web */}
        <div className={classes.nameBlock}>
          <Tooltip
            title={`${contactDisplayName} · Ticket #${ticket.id} · ver datos del contacto`}
            arrow
          >
            <Typography className={classes.name}>{contactDisplayName}</Typography>
          </Tooltip>
          {presence && (
            <Typography className={classes.presence}>
              {presenceText(presence)}
            </Typography>
          )}
          {!presence && attendingByOther && (
            <Typography className={classes.attendingBy}>
              {i18n.t("ticketAttending.header", {
                name: ticket.user?.name || i18n.t("ticketAttending.someone"),
              })}
            </Typography>
          )}
        </div>

        {/* Identificador del objeto (patente, n° de serie…) al lado del nombre:
            el mostrador busca por eso. Sólo TurnosIA con config; si no, nada. */}
        {!ticket?.isGroup && <ContactIdentificador number={contact?.number} />}

        {/* Badge tipo de cliente */}
        <Tooltip title="Tipo de cliente" arrow>
          <Chip
            label={localContactType?.name || "Tipo cliente"}
            size="small"
            className={classes.chip}
            style={{
              backgroundColor: localContactType?.color || "#90a4ae",
              color: "#fff",
            }}
            onClick={openTypePicker}
          />
        </Tooltip>

        {/* Tags del ticket (chips con ×) */}
        {ticketTags.map(tag => (
          <Chip
            key={tag.id}
            label={tag.name}
            size="small"
            className={classes.chip}
            style={{ backgroundColor: tag.color, color: "#fff" }}
            onDelete={() => handleRemoveTicketTag(tag.id)}
            deleteIcon={<CloseIcon style={{ fontSize: 10, color: "#fff" }} />}
            onClick={e => e.stopPropagation()}
          />
        ))}

        {/* Razón del contacto */}
        {ticket?.contactReason && (
          <Chip
            label={ticket.contactReason.name}
            size="small"
            className={classes.chip}
            style={{ backgroundColor: "#667eea", color: "#fff" }}
            onClick={e => e.stopPropagation()}
          />
        )}

        {/* Separador + contact tags (dashed pills con hover ×) + botón + */}
        {contactTags.length > 0 && (
          <>
            <span className={classes.separator}>|</span>
            {visibleContactTags.map(tag => (
              <span
                key={tag.id}
                className={classes.contactTagPill}
                style={{ borderColor: tag.color ? `${tag.color}88` : "rgba(0,0,0,0.32)" }}
                onClick={e => e.stopPropagation()}
              >
                {tag.name}
                <span
                  className={classes.pillRemove}
                  onClick={e => handleRemoveContactTag(e, tag.id)}
                  title="Quitar tag"
                >
                  <CloseIcon style={{ fontSize: 9 }} />
                </span>
              </span>
            ))}
            {extraContactTags > 0 && (
              <span className={classes.contactTagPill}>
                +{extraContactTags}
              </span>
            )}
          </>
        )}
        {/* Botón + para agregar contact tag */}
        <Tooltip title="Agregar etiqueta al contacto" arrow>
          <span
            className={classes.addTagBtn}
            onClick={openTagPicker}
          >
            <AddIcon style={{ fontSize: 12 }} />
          </span>
        </Tooltip>
      </div>

      {/* Popover selector de tipo de cliente */}
      <Popover
        open={Boolean(typeAnchorEl)}
        anchorEl={typeAnchorEl}
        onClose={() => setTypeAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ className: classes.popoverPaper }}
        onClick={e => e.stopPropagation()}
      >
        <Typography variant="caption" style={{ padding: "4px 8px", display: "block", fontWeight: 600 }}>
          Tipo de cliente
        </Typography>
        <div className={classes.tagOption} onClick={() => handleSelectType(null)}>
          <span style={{ flex: 1, fontStyle: "italic", color: "#999" }}>Sin definir</span>
          {!localContactType && <CloseIcon style={{ fontSize: 12 }} />}
        </div>
        {contactTypes.map(t => (
          <div key={t.id} className={classes.tagOption} onClick={() => handleSelectType(t.id)}>
            <span className={classes.tagDot} style={{ backgroundColor: t.color }} />
            <span style={{ flex: 1 }}>{t.name}</span>
            {localContactType?.id === t.id && <CloseIcon style={{ fontSize: 12 }} />}
          </div>
        ))}
      </Popover>

      {/* Popover selector de contact tag */}
      <Popover
        open={Boolean(tagPickerAnchorEl)}
        anchorEl={tagPickerAnchorEl}
        onClose={() => setTagPickerAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ className: classes.tagPickerPaper }}
        onClick={e => e.stopPropagation()}
      >
        <Typography variant="caption" style={{ padding: "4px 8px", display: "block", fontWeight: 600 }}>
          Etiquetas del contacto
        </Typography>
        {allTags.length === 0 && (
          <div style={{ padding: "4px 8px", color: "#999", fontSize: 12 }}>Sin etiquetas disponibles</div>
        )}
        {allTags
          .filter(t => !contactTags.some(ct => ct.id === t.id))
          .map(t => (
            <div key={t.id} className={classes.tagOption} onClick={() => handleAddContactTag(t.id)}>
              <span className={classes.tagDot} style={{ backgroundColor: t.color }} />
              <span style={{ flex: 1 }}>{t.name}</span>
            </div>
          ))}
        {allTags.length > 0 && allTags.every(t => contactTags.some(ct => ct.id === t.id)) && (
          <div style={{ padding: "4px 8px", color: "#999", fontSize: 12 }}>
            Todos los tags asignados
          </div>
        )}
      </Popover>
    </div>
  );
};

export default TicketInfo;
